import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  adminApi,
  type DeviceListItem,
  type DeviceProjectMapEntry,
  type Project,
  type Team,
} from '../api/admin';
import { DataListFrame, sortArrow, useListControls } from '../components/data-list';
import { Modal } from '../components/modal';
import { teamLabel } from '../lib/team-icon';

// チーム割当 = デバイスマウント(device_project_map) ⇄ 担当チームのマッチング（タスク#206で改定）。
// 「このプロジェクトのこのデバイスマウントは、このチームが担当する」を決める。1 デバイスマウント = 高々1チーム。
// 人間はデバイスをあまり意識しない（プロジェクトで考える）ので、一覧はプロジェクト単位で見せ、
// 各プロジェクト配下のデバイスマウントごとに担当チームを割り当てる。
// 旧実装（エージェント→プロジェクトの projectId 割当）は廃止。担当プロジェクトはチーム所属から派生する。

// デバイスマウント1件（どのデバイスのどのマウントポイントか）を、所属デバイス名つきで持つ行。
interface DirRow extends DeviceProjectMapEntry {
  deviceName: string;
}

type AssignSortField = 'id' | 'name' | 'type';
const SORT_OPTIONS: { value: AssignSortField; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'name', label: 'プロジェクト名' },
  { value: 'type', label: '区分' },
];

export function AssignPage() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['devices'] });
    qc.invalidateQueries({ queryKey: ['projects'] });
    qc.invalidateQueries({ queryKey: ['teams'] });
  };

  const devicesQuery = useQuery({ queryKey: ['devices'], queryFn: adminApi.listDevices });
  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: adminApi.listProjects });
  const teamsQuery = useQuery({ queryKey: ['teams'], queryFn: adminApi.listTeams });
  const mastersQuery = useQuery({ queryKey: ['agent-masters'], queryFn: adminApi.listAgentMasters });

  const assign = useMutation({
    mutationFn: (vars: { mapId: number; teamId: number | null }) =>
      adminApi.assignPathTeam(vars.mapId, vars.teamId),
    onSuccess: invalidate,
  });

  const updateHelloMsg = useMutation({
    mutationFn: (vars: { mapId: number; helloMessage: string | null }) =>
      adminApi.updateHelloMessage(vars.mapId, vars.helloMessage),
    onSuccess: invalidate,
  });

  // 1DIR複数チーム: primary 以外の「追加チーム」集合を置換する。
  const setTeams = useMutation({
    mutationFn: (vars: { mapId: number; teamIds: number[] }) =>
      adminApi.setPathTeams(vars.mapId, vars.teamIds),
    onSuccess: invalidate,
  });

  const devices = devicesQuery.data ?? [];
  const allProjects = projectsQuery.data ?? [];
  const teams = teamsQuery.data ?? [];
  const masterTypes = (mastersQuery.data?.types ?? []).filter((t) => t.enabled);
  const typeLabelOf = (code: string) =>
    mastersQuery.data?.types.find((t) => t.code === code)?.label ?? code;
  // 区分(type) = ドロップダウン絞り込み（マスタ）。'' は指定なし。
  const [typeFilter, setTypeFilter] = useState('');

  // 全デバイスマウントを「所属デバイス名つき」でフラットにし、projectId ごとに引けるようにする。
  const dirsByProject = useMemo(() => {
    const map = new Map<number, DirRow[]>();
    for (const d of devices as DeviceListItem[]) {
      const deviceName = d.nickname || d.name;
      for (const e of d.deviceProjectMap) {
        if (e.projectId == null) continue; // プロジェクト未割当のデバイスマウントは出さない
        const list = map.get(e.projectId) ?? [];
        list.push({ ...e, deviceName });
        map.set(e.projectId, list);
      }
    }
    return map;
  }, [devices]);

  const controls = useListControls<AssignSortField>('konecco.list.assign', {
    q: '',
    sortBy: 'id',
    sortDir: 'asc',
    page: 1,
    pageSize: 20,
  });

  const filtered = useMemo(() => {
    const q = controls.q.trim().toLowerCase();
    let list = allProjects;
    if (q)
      list = list.filter(
        (p) => p.name.toLowerCase().includes(q) || (p.nickname ?? '').toLowerCase().includes(q),
      );
    if (typeFilter) list = list.filter((p) => p.type === typeFilter);
    const dir = controls.sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      if (controls.sortBy === 'name') return a.name.localeCompare(b.name) * dir;
      if (controls.sortBy === 'type') return a.type.localeCompare(b.type) * dir;
      return (a.id - b.id) * dir;
    });
    return list;
  }, [allProjects, typeFilter, controls.q, controls.sortBy, controls.sortDir]);

  const total = filtered.length;
  const pageItems = useMemo(() => {
    const start = (controls.page - 1) * controls.pageSize;
    return filtered.slice(start, start + controls.pageSize);
  }, [filtered, controls.page, controls.pageSize]);

  const [manageId, setManageId] = useState<number | null>(null);
  const manageProject = allProjects.find((p) => p.id === manageId) ?? null;

  const loading = projectsQuery.isLoading || devicesQuery.isLoading;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="k-muted" style={{ margin: 0, fontSize: 13 }}>
        プロジェクト配下の各デバイスマウントに担当チームを割り当てます。各行の「チームを割り当て」で
        デバイスマウントごとに割当・解除します。デバイスマウントのプロジェクトへの割当は「プロジェクト」、チームの登録は「チーム」メニューです。
      </p>
      {assign.error && <p className="k-error">{(assign.error as Error).message}</p>}

      <DataListFrame
        title="チームプロジェクトアサイン（プロジェクト別）"
        total={total}
        loading={loading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="あいまい検索…（プロジェクト名・愛称）"
        filters={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="k-muted" style={{ fontSize: 12 }}>
              区分
            </span>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                controls.setPage(1);
              }}
              style={{ padding: '7px 10px', fontSize: 13 }}
            >
              <option value="">すべて</option>
              {masterTypes.map((t) => (
                <option key={t.id} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        }
      >
        <div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(180px, 0.6fr) 110px minmax(0, 2fr) 120px',
              gap: 12,
              padding: '8px 14px',
              background: '#f5f7fa',
              fontSize: 12,
              fontWeight: 600,
              color: '#64748b',
              borderBottom: '1px solid #e7ebf0',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <div
              onClick={() => controls.toggleSort('name')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="クリックで並び替え"
            >
              プロジェクト名
              <span style={{ color: '#2563eb' }}>{sortArrow(controls, 'name')}</span>
            </div>
            <div
              onClick={() => controls.toggleSort('type')}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              title="クリックで並び替え"
            >
              区分
              <span style={{ color: '#2563eb' }}>{sortArrow(controls, 'type')}</span>
            </div>
            <div>担当チーム（デバイスマウント単位）</div>
            <div style={{ textAlign: 'right' }}>操作</div>
          </div>
          {pageItems.map((p, i) => (
            <AssignRow
              key={p.id}
              project={p}
              typeLabel={typeLabelOf(p.type)}
              dirs={dirsByProject.get(p.id) ?? []}
              last={i === pageItems.length - 1}
              onManage={() => setManageId(p.id)}
            />
          ))}
        </div>
      </DataListFrame>

      {manageProject && (
        <AssignManageDialog
          project={manageProject}
          typeLabel={typeLabelOf(manageProject.type)}
          dirs={dirsByProject.get(manageProject.id) ?? []}
          teams={teams}
          busy={assign.isPending || updateHelloMsg.isPending || setTeams.isPending}
          onClose={() => setManageId(null)}
          onAssign={(mapId, teamId) => assign.mutate({ mapId, teamId })}
          onUpdateHelloMessage={(mapId, msg) => updateHelloMsg.mutate({ mapId, helloMessage: msg })}
          onSetTeams={(mapId, teamIds) => setTeams.mutate({ mapId, teamIds })}
        />
      )}
    </div>
  );
}

function AssignRow({
  project,
  typeLabel,
  dirs,
  last,
  onManage,
}: {
  project: Project;
  typeLabel: string;
  dirs: DirRow[];
  last: boolean;
  onManage: () => void;
}) {
  const assignedCount = dirs.filter((d) => d.teamId != null).length;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(180px, 0.6fr) 110px minmax(0, 2fr) 120px',
        gap: 12,
        padding: '12px 14px',
        alignItems: 'flex-start',
        background: '#fff',
        borderBottom: last ? 'none' : '1px solid #eef2f7',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <strong style={{ fontSize: 15 }}>{project.nickname || project.name}</strong>
        {project.nickname && (
          <span
            className="k-muted"
            style={{ fontSize: 12, marginLeft: 8, fontFamily: 'monospace' }}
          >
            #{project.name}
          </span>
        )}
        <div className="k-muted" style={{ fontSize: 12, marginTop: 2 }}>
          デバイスマウント {dirs.length} 件中 {assignedCount} 件割当済み
        </div>
      </div>
      <div>
        <span className="k-badge">{typeLabel}</span>
      </div>
      {/* マウント単位の明細：デバイス / マウントポイント / 用途メモ / 担当チーム を1行ずつ。 */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
        {dirs.length === 0 ? (
          <span className="k-muted" style={{ fontSize: 13 }}>
            このプロジェクトにデバイスマウントがありません
          </span>
        ) : (
          dirs.map((d) => (
            <div
              key={d.id}
              style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 12.5 }}
            >
              <span className="k-muted" style={{ fontSize: 11 }}>
                {d.deviceName}
              </span>
              <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{d.mountPoint.name}</span>
              {d.purpose ? (
                <span
                  style={{
                    padding: '1px 7px',
                    borderRadius: 999,
                    background: '#dcfce7',
                    color: '#166534',
                    fontSize: 11,
                  }}
                >
                  {d.purpose}
                </span>
              ) : (
                <span className="k-muted" style={{ fontSize: 11 }}>
                  用途なし
                </span>
              )}
              <span className="k-muted">→</span>
              {d.team ? (
                <span
                  className="k-badge"
                  style={{ background: '#eef2ff', color: '#3730a3' }}
                >
                  {teamLabel(d.team)}
                </span>
              ) : (
                <span
                  style={{
                    padding: '1px 7px',
                    borderRadius: 999,
                    background: '#fef2f2',
                    color: '#b91c1c',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  未割当
                </span>
              )}
            </div>
          ))
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn-ghost" type="button" onClick={onManage} disabled={dirs.length === 0}>
          チームを割り当て
        </button>
      </div>
    </div>
  );
}

// 割り当てダイアログ：プロジェクト配下の各デバイスマウントにチームを割り当て（即時保存）。1 デバイスマウント = 高々1チーム。
function AssignManageDialog({
  project,
  typeLabel,
  dirs,
  teams,
  busy,
  onClose,
  onAssign,
  onUpdateHelloMessage,
  onSetTeams,
}: {
  project: Project;
  typeLabel: string;
  dirs: DirRow[];
  teams: Team[];
  busy: boolean;
  onClose: () => void;
  onAssign: (mapId: number, teamId: number | null) => void;
  onUpdateHelloMessage: (mapId: number, helloMessage: string | null) => void;
  onSetTeams: (mapId: number, teamIds: number[]) => void;
}) {
  const [helloEdits, setHelloEdits] = useState<Record<number, string>>({});
  return (
    <Modal
      title={
        <span>
          デバイスマウントにチームを割り当て — {project.nickname || project.name}{' '}
          <span className="k-badge" style={{ fontWeight: 400 }}>
            {typeLabel}
          </span>
        </span>
      }
      onClose={onClose}
      width={620}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {dirs.length === 0 ? (
          <p className="k-muted" style={{ fontSize: 13, margin: 0 }}>
            このプロジェクトに割り当てられたデバイスマウントがありません。「プロジェクト」メニューでデバイスマウントを割り当ててください。
          </p>
        ) : (
          dirs.map((d) => (
            <div
              key={d.id}
              style={{
                padding: '8px 10px',
                background: '#fff',
                border: '1px solid #eef2f7',
                borderRadius: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{d.deviceName}</span>
                    <span className="k-muted" style={{ fontSize: 12 }}>{d.mountPoint.name}</span>
                    {/* 用途/役割。デバイスまたぎで同じマウントポイントが並ぶとき「何用か」をここで判別する。 */}
                    {d.purpose && (
                      <span
                        style={{
                          padding: '1px 7px',
                          borderRadius: 999,
                          background: '#dcfce7',
                          color: '#166534',
                          fontSize: 11,
                        }}
                      >
                        {d.purpose}
                      </span>
                    )}
                  </div>
                  <div
                    className="k-muted"
                    style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}
                  >
                    {d.mountPoint.path}
                  </div>
                </div>
                <select
                  value={d.teamId ?? ''}
                  disabled={busy}
                  onChange={(e) => onAssign(d.id, e.target.value ? Number(e.target.value) : null)}
                  style={{ flex: 1, maxWidth: 280, padding: '6px 10px', fontSize: 13 }}
                >
                  <option value="">（未アサイン）</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {teamLabel(t)}
                    </option>
                  ))}
                </select>
              </div>
              {/* 1DIR複数チーム: primary 以外の「追加チーム」トグル。
                  同じデバイス/フォルダを複数チームで使い分ける（例: 精査部＋壁打ち部）。
                  実行時のチーム反映はプロンプト注入なので同フォルダでも衝突しない。 */}
              <div style={{ marginTop: 6 }}>
                <div className="k-muted" style={{ fontSize: 11, marginBottom: 4 }}>
                  追加チーム（このDIRを複数チームで使い分け）
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(() => {
                    // 現在の追加チーム = teams[](primary+追加) から primary を除いた集合。
                    const extras = new Set(
                      (d.teams ?? [])
                        .map((t) => t.id)
                        .filter((id) => id !== d.teamId),
                    );
                    return teams
                      .filter((t) => t.id !== d.teamId) // primary は上の select 側
                      .map((t) => {
                        const on = extras.has(t.id);
                        return (
                          <button
                            key={t.id}
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              const next = new Set(extras);
                              if (on) next.delete(t.id);
                              else next.add(t.id);
                              onSetTeams(d.id, [...next]);
                            }}
                            style={{
                              padding: '3px 10px',
                              fontSize: 12,
                              borderRadius: 999,
                              border: on ? '1px solid #2563eb' : '1px solid #e2e8f0',
                              background: on ? '#dbeafe' : '#fff',
                              color: on ? '#1e40af' : '#475569',
                              cursor: busy ? 'default' : 'pointer',
                            }}
                          >
                            {on ? '✓ ' : ''}
                            {teamLabel(t)}
                          </button>
                        );
                      });
                  })()}
                </div>
              </div>
              {/* hello メッセージ編集（Claudeへの初期指示） */}
              <div style={{ marginTop: 6, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                <textarea
                  rows={2}
                  placeholder="helloメッセージ（空欄 = 指示なし）"
                  disabled={busy}
                  value={helloEdits[d.id] ?? (d.helloMessage ?? '')}
                  onChange={(e) =>
                    setHelloEdits((prev) => ({ ...prev, [d.id]: e.target.value }))
                  }
                  style={{
                    flex: 1,
                    padding: '5px 8px',
                    fontSize: 12,
                    resize: 'vertical',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontFamily: 'monospace',
                  }}
                />
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={busy || (helloEdits[d.id] ?? (d.helloMessage ?? '')) === (d.helloMessage ?? '')}
                  onClick={() => {
                    const val = helloEdits[d.id] ?? '';
                    onUpdateHelloMessage(d.id, val.trim() || null);
                  }}
                  style={{ fontSize: 12, whiteSpace: 'nowrap', alignSelf: 'center' }}
                >
                  保存
                </button>
              </div>
            </div>
          ))
        )}
        {teams.length === 0 && dirs.length > 0 && (
          <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>
            チームがまだありません。「チーム」メニューで作成してください。
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            閉じる
          </button>
        </div>
      </div>
    </Modal>
  );
}
